import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';
import { endSession } from '@/lib/session';
import {
  anchorFromServer,
  clearIdleRecord,
  evaluate,
  IDLE_RECORD_KEY,
  isTracking,
  noteActivity,
  noteHeartbeatSent,
  type IdlePhase,
} from '@/lib/idleSession';

/**
 * Activity events.
 *
 * Chosen deliberately; each omission is as considered as each inclusion.
 *
 *  pointerdown — covers mouse, touch and pen in one listener
 *  keydown     — typing
 *  input       — the ONLY signal from OS dictation, IME composition, and
 *                paste-from-menu. Legacy bound `click` alone, so a user typing a
 *                long note got signed out mid-sentence; that bug is not worth
 *                reproducing.
 *  wheel       — trackpad/mouse scrolling
 *  scroll      — needs capture:true. The app's scroll container is
 *                `<main class="overflow-y-auto">` in Layout, not the document,
 *                and scroll doesn't bubble — but the capture phase still reaches
 *                window. Without it, reading a long chart counts as idle.
 *
 * NOT included, on purpose:
 *  mousemove         — a bumped desk or a drifting mouse is not presence, and an
 *                      unattended workstation is exactly the threat here.
 *  visibilitychange,
 *  focus             — these are EVALUATION triggers, below. Treating them as
 *                      activity would reset the timer on resume-from-sleep, i.e.
 *                      at the precise moment the session should expire.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'input', 'wheel', 'scroll'] as const;

/**
 * Events that mean "re-check now", not "the user is here".
 *
 * These carry the whole background story. Chrome throttles timers in hidden tabs
 * to ~1/min and can freeze them entirely under Page Lifecycle; iOS Safari
 * suspends outright. So the interval alone cannot be relied on to notice a
 * four-hour sleep — but these fire the instant the tab comes back, before the
 * user can read anything on screen. `pageshow` additionally covers bfcache
 * restores, where the page returns with a frozen timer and a live React tree.
 */
const EVALUATE_EVENTS = ['visibilitychange', 'focus', 'pageshow', 'online'] as const;

const TICK_MS = 1_000;

export interface IdleLogoutState {
  phase: IdlePhase;
  secondsRemaining: number;
  staySignedIn: () => void;
  signOutNow: () => void;
}

/**
 * Drives the idle auto-logout. Mounted exactly once, by SessionWatcher.
 *
 * A single 1Hz interval plus a wall-clock comparison, rather than a chain of
 * setTimeouts: correctness doesn't depend on when the tick fires (a late tick
 * computes the same answer, just later), the 1Hz tick is needed for the countdown
 * anyway, and a timeout chain would have to be torn down and re-armed on every
 * activity event — up to a dozen clear/set pairs a minute, doubled under
 * StrictMode's double-invoked effects.
 */
export function useIdleLogout(): IdleLogoutState {
  const token = useAuthStore((s) => s.token);
  const session = useAuthStore((s) => s.session);
  const userId = useAuthStore((s) => s.user?.id ?? 0);
  const accountId = useAuthStore((s) => s.accountId ?? 0);

  const [phase, setPhase] = useState<IdlePhase>('inactive');
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  /** Suppresses ambient activity while the warning is up — see below. */
  const warningOpen = useRef(false);
  /** Guards against overlapping heartbeats. */
  const heartbeatInFlight = useRef(false);

  const enabled = Boolean(token && session && session.idleTimeoutSeconds > 0);
  const identity = { userId, accountId };

  const sendHeartbeat = useCallback(async () => {
    if (heartbeatInFlight.current) return;
    heartbeatInFlight.current = true;
    // Stamp before awaiting, so a slow round-trip can't let the throttle fire
    // a second one.
    noteHeartbeatSent();
    try {
      const ack = await authApi.heartbeat();
      anchorFromServer({
        expiresInSeconds: ack.expiresInSeconds,
        warnSeconds: ack.warnSeconds,
        userId,
        accountId,
      });
      warningOpen.current = false;
      setPhase('active');
    } catch {
      // A failed heartbeat means the window keeps running down — correct. If the
      // session is genuinely gone, apiClient's 401 handling ends it.
    } finally {
      heartbeatInFlight.current = false;
    }
  }, [userId, accountId]);

  // ── Arm / disarm ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !session) {
      setPhase('inactive');
      return;
    }

    // Open a record if there isn't one (first paint after login, or after a
    // reload). The login payload's policy stands in for a heartbeat ack: the
    // server opened its own record when it minted the token.
    if (!isTracking()) {
      anchorFromServer({
        expiresInSeconds: session.idleTimeoutSeconds,
        warnSeconds: session.warnSeconds,
        userId,
        accountId,
      });
    }
  }, [enabled, session, userId, accountId]);

  // ── The tick ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const check = () => {
      const result = evaluate(Date.now(), identity);

      setPhase(result.phase);
      setSecondsRemaining(result.secondsRemaining);
      warningOpen.current = result.phase === 'warn';

      if (result.phase === 'expired') {
        // The client noticed first, so tell the server to revoke rather than
        // waiting for it to refuse us.
        endSession('idle', true);
        return;
      }

      if (result.shouldHeartbeat && document.visibilityState === 'visible') {
        void sendHeartbeat();
      }
    };

    check();
    const id = window.setInterval(check, TICK_MS);

    const onEvaluate = () => check();
    for (const event of EVALUATE_EVENTS) {
      window.addEventListener(event, onEvaluate);
    }
    document.addEventListener('visibilitychange', onEvaluate);

    return () => {
      window.clearInterval(id);
      for (const event of EVALUATE_EVENTS) {
        window.removeEventListener(event, onEvaluate);
      }
      document.removeEventListener('visibilitychange', onEvaluate);
    };
  }, [enabled, sendHeartbeat, userId, accountId]);

  // ── Activity listeners ────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const onActivity = () => {
      // While the countdown is up, ambient interaction must NOT silently cancel
      // it. Otherwise a stray click resets the local timer without sending a
      // heartbeat, the client goes quiet and confident, and the server expires
      // anyway — a logout with no warning. The only way out of the warning is
      // the explicit button, which heartbeats.
      if (warningOpen.current) return;
      noteActivity();
    };

    // capture:true so a component calling stopPropagation() can't hide activity
    // from us; passive:true because we never preventDefault.
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { capture: true, passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity, { capture: true });
      }
    };
  }, [enabled]);

  // ── Cross-tab ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;

    const onStorage = (event: StorageEvent) => {
      // Another tab signed out: zustand's persist middleware doesn't subscribe to
      // storage events, so without this the other tabs keep their in-memory token
      // and go on rendering PHI until their next 401.
      if (event.key === 'ftp-auth') {
        // localStorage.clear() surfaces as newValue === null.
        if (event.newValue === null) {
          endSession('user');
          return;
        }
        try {
          const parsed = JSON.parse(event.newValue) as { state?: { token?: string | null } };
          // Diff the token specifically: persist rewrites this key on EVERY state
          // change (including the /auth/me refresh in Layout), so reacting to any
          // event here would thrash.
          if (parsed?.state && parsed.state.token == null) {
            endSession('user');
          }
        } catch {
          // Unparseable — leave this tab alone rather than guess.
        }
        return;
      }

      // Activity or a new anchor from another tab: re-evaluate immediately so all
      // tabs share one deadline and warn together.
      if (event.key === IDLE_RECORD_KEY) {
        const result = evaluate(Date.now(), identity);
        setPhase(result.phase);
        setSecondsRemaining(result.secondsRemaining);
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token, userId, accountId]);

  // Drop the shared record when the session is gone, so a later sign-in on this
  // browser can't inherit a stale deadline.
  useEffect(() => {
    if (!token) clearIdleRecord();
  }, [token]);

  const staySignedIn = useCallback(() => {
    warningOpen.current = false;
    setPhase('active');
    void sendHeartbeat();
  }, [sendHeartbeat]);

  const signOutNow = useCallback(() => {
    endSession('user', true);
  }, []);

  return { phase, secondsRemaining, staySignedIn, signOutNow };
}
